import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import { ImageGallery } from "@/@types";
import { FallbackImage } from "@/components/common";
import { useRef } from "react";

type Props = {
  imagesGallery: ImageGallery[];
  imageWidth?: number;
  imageHeight?: number;
  objectFit?: "cover" | "contain";
  enableFullscreen: boolean;
  setFullscreenImage: (src: string | null) => void;
  setActiveIndex: (index: number) => void;
  thumbsSwiper: any;
};

export default function MainSlider({
  imagesGallery,
  imageWidth,
  imageHeight,
  objectFit,
  enableFullscreen,
  setFullscreenImage,
  setActiveIndex,
  thumbsSwiper,
}: Props) {
  // Referencia para la instancia de Swiper
  const swiperRef = useRef<any>(null);

  return (
    <div className="relative">
      <Swiper
        modules={[Navigation, Thumbs]}
        navigation={false} // Deshabilitamos la navegación interna
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        loop={imagesGallery.length > 1}
        thumbs={{ swiper: thumbsSwiper }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="mainSwiper w-full rounded-lg overflow-hidden"
      >
        {imagesGallery.map((image, index) =>
          image.src ? (
            <SwiperSlide key={index}>
              <div
                className="relative w-full cursor-pointer"
                style={{
                  height: imageHeight || "auto",
                  minHeight: imageHeight ? `${imageHeight}px` : "200px",
                  aspectRatio: !imageHeight ? "16/9" : "auto",
                }}
                onClick={() =>
                  enableFullscreen && setFullscreenImage(image.src)
                }
              >
                {imageWidth && imageHeight ? (
                  <FallbackImage
                    src={image.src}
                    alt={`Imagen ${index + 1}`}
                    width={imageWidth}
                    height={imageHeight}
                    unoptimized={image.unoptimized ?? false}
                    style={{ objectFit, width: "100%", height: "auto" }}
                    priority={index === 0}
                    className="rounded-lg"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px"
                  />
                ) : (
                  <FallbackImage
                    src={image.src}
                    alt={`Imagen ${index + 1}`}
                    fill
                    unoptimized={image.unoptimized ?? false}
                    style={{ objectFit }}
                    priority={index === 0}
                    className="rounded-lg"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px"
                  />
                )}
              </div>
            </SwiperSlide>
          ) : null
        )}
      </Swiper>

      {/* Flecha izquierda: contenedor pegado al borde izquierdo, con gradiente de izquierda a derecha */}
      <div
        onClick={() => swiperRef.current?.slidePrev()}
        className="absolute top-0 bottom-0 left-0 flex items-center justify-start z-20 cursor-pointer"
        style={{
          background:
            "linear-gradient(to right,  rgba(80, 80, 80, 0.4) 0%, rgba(130,130,130,0) 100%)",
          width: "6rem"
        }}
      >
        <svg
          style={{ width: "64px", height: "64px" }}
          className="text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </div>

      {/* Flecha derecha: contenedor pegado al borde derecho, con gradiente de derecha a izquierda */}
      <div
        onClick={() => swiperRef.current?.slideNext()}
        className="absolute top-0 bottom-0 right-0 flex items-center justify-end z-20 cursor-pointer"
        style={{
          background:
            "linear-gradient(to left, rgba(80, 80, 80, 0.4) 0%, rgba(130,130,130,0) 100%)",
          width: "6rem"
        }}
      >
        <svg
          style={{ width: "64px", height: "64px" }}
          className="text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>

      {/* Ocultar cualquier flecha predeterminada (por si acaso) */}
      <style jsx global>{`
        .swiper-button-prev,
        .swiper-button-next {
          display: none !important;
        }
      `}</style>
    </div>
  );
}
